import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { Header } from './Header';
import { Footer } from './Footer';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

export const Partner = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [contactForm, setContactForm] = useState({
    company_name: '',
    email: '',
    phone: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);

    try {
      await axios.post(`${API_URL}/contact`, {
        company_name: contactForm.company_name,
        email: contactForm.email,
        phone: contactForm.phone || undefined,
      });
      setContactSuccess(true);
      setContactForm({ company_name: '', email: '', phone: '' });
    } catch (err) {
      setContactError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-[#F9F7F0]">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-ngbc-primary hover:text-ngbc-accent font-medium mb-8 flex items-center gap-1"
          >
            ← {t('team.teamZurueck')}
          </button>

          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-bold text-[#005F5F] mb-3">{t('home.partnerWerdenSectionTitle')}</h1>
            <p className="text-base text-[#005F5F]/85">{t('home.partnerWerdenSectionSub')}</p>
          </div>

          <div className="space-y-10">
            <div className="relative rounded-3xl shadow-xl bg-[#8CD7D1] p-4 pt-14 sm:p-5 sm:pt-16">
              <h2 className="absolute top-4 left-5 sm:top-5 sm:left-8 text-white font-bold text-sm sm:text-base uppercase tracking-widest">
                {t('home.keyFactsCaps')}
              </h2>
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-inner border border-white/80">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div key={n} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#60afb6] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.5} />
                      </div>
                      <span className="text-[#005F5F] text-sm sm:text-base leading-relaxed font-medium">
                        {t(`home.partnerFact${n}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative rounded-3xl shadow-xl bg-[#8CD7D1] p-4 pt-14 sm:p-5 sm:pt-16">
              <h2 className="absolute top-4 left-5 sm:top-5 sm:left-8 text-white font-bold text-sm sm:text-base uppercase tracking-widest">
                {t('home.kontaktformularCaps')}
              </h2>
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-inner border border-white/80">
                {contactSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-ngbc-green mb-2">Thank you!</h3>
                    <p className="text-gray-600 mb-4">We will contact you soon.</p>
                    <button
                      type="button"
                      onClick={() => setContactSuccess(false)}
                      className="text-ngbc-green hover:text-ngbc-accent font-medium"
                    >
                      Send another inquiry
                    </button>
                  </div>
                ) : (
                  <form className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" onSubmit={handleContactSubmit}>
                    {contactError && (
                      <div className="md:col-span-3 text-red-600 text-sm bg-red-50 rounded-lg p-3">
                        {contactError}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-[#005F5F] mb-2">{t('home.firmaStar')}</label>
                      <input
                        type="text"
                        required
                        value={contactForm.company_name}
                        onChange={(e) => setContactForm({ ...contactForm, company_name: e.target.value })}
                        className="w-full px-4 py-3 text-sm text-gray-800 bg-white border border-gray-200 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-[#8CD7D1] focus:border-transparent"
                        placeholder="Firma GmbH"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#005F5F] mb-2">{t('home.emailStar')}</label>
                      <input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-3 text-sm text-gray-800 bg-white border border-gray-200 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-[#8CD7D1] focus:border-transparent"
                        placeholder="kontakt@firma.de"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#005F5F] mb-2">{t('home.telefonOptionalField')}</label>
                      <input
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="w-full px-4 py-3 text-sm text-gray-800 bg-white border border-gray-200 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-[#8CD7D1] focus:border-transparent"
                        placeholder="+49 123 456789"
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-center md:justify-start pt-2">
                      <button
                        type="submit"
                        disabled={contactSubmitting}
                        className="bg-ngbc-highlight hover:bg-ngbc-accent disabled:bg-gray-400 text-white font-bold text-sm py-3 px-10 rounded-full transition-colors shadow-md flex items-center gap-2"
                      >
                        {contactSubmitting ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Sending...
                          </>
                        ) : (
                          t('home.absenden')
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};
